import { useEffect, useMemo, useState } from "react";
import { getItems } from "../api";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function getBucket(due) {
  const now = new Date();
  const dueDate = new Date(due);

  if (Number.isNaN(dueDate.getTime())) return "later";

  if (dueDate <= now) return "overdue";

  const diffMs = dueDate.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours <= 24) return "today";
  if (diffHours <= 24 * 7) return "thisWeek";
  return "later";
}

function sortByDueAsc(items) {
  return [...items].sort((a, b) => {
    const aTime = new Date(a.due || 0).getTime();
    const bTime = new Date(b.due || 0).getTime();
    return aTime - bTime;
  });
}

export default function SchedulePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadItems() {
    setLoading(true);
    setError("");

    try {
      const data = await getItems(200);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  const grouped = useMemo(() => {
    const sorted = sortByDueAsc(items);

    return {
      overdue: sorted.filter((item) => getBucket(item.due) === "overdue"),
      today: sorted.filter((item) => getBucket(item.due) === "today"),
      thisWeek: sorted.filter((item) => getBucket(item.due) === "thisWeek"),
      later: sorted.filter((item) => getBucket(item.due) === "later"),
    };
  }, [items]);

  function renderSection(title, list) {
    return (
      <section className="card">
        <div className="row-between">
          <h3>{title}</h3>
          <span className="badge">{list.length}</span>
        </div>

        {list.length === 0 ? (
          <div className="muted">No items here.</div>
        ) : (
          <div className="stack-sm">
            {list.map((item) => (
              <div key={item._id} className="result-item">
                <div className="row-between">
                  <strong>{item.item}</strong>
                  <span className="badge">{item.type}</span>
                </div>

                <div>translate: {item.translate}</div>
                <div>due: {formatDate(item.due)}</div>
                <div>introSeen: {String(item.introSeen)}</div>
                <div>totalReviews: {item.totalReviews ?? 0}</div>
                <div>lastRating: {item.lastRating ?? "—"}</div>
                <div>lastReviewedAt: {formatDate(item.lastReviewedAt)}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <div className="stack">
      <section className="card">
        <div className="row-between wrap">
          <div>
            <h2>Schedule</h2>
            <p className="muted">
              Here you can see when cards are due again.
            </p>
          </div>

          <button onClick={loadItems} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh schedule"}
          </button>
        </div>

        {error ? <div className="alert error">{error}</div> : null}
      </section>

      {renderSection("Need review now", grouped.overdue)}
      {renderSection("Within 24 hours", grouped.today)}
      {renderSection("Within 7 days", grouped.thisWeek)}
      {renderSection("Later", grouped.later)}
    </div>
  );
}